import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Box,
  Avatar,
  Button,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Cake as CakeIcon,
  Person as PersonIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';

const ExternalCustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    const fetchCustomerDetail = async () => {
      try {
        setLoading(true);
        const response = await fetch(`https://apidashboard.eurekagroup.id/external-customers/get/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        setCustomer(result.data || result);
      } catch (error) {
        console.error('Error fetching customer detail:', error);
        Swal.fire({
          ...SwalConfig.error,
          title: 'Error!',
          text: `Gagal memuat detail customer: ${error.message}`
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCustomerDetail();
    }
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBirthDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return 'success';
      case 0:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return 'Active';
      case 0:
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 1:
        return 'info';
      case 2:
        return 'primary';
      case 3:
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Memuat data customer...
        </Typography>
      </Box>
    );
  }

  if (!customer) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Customer tidak ditemukan
        </Typography>
      </Box>
    );
  }

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }} className="bg-gradient-to-br from-white to-blue-50">
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <PeopleIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Detail External Customer
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Informasi lengkap customer eksternal
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/eureka-plus/external-customers')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Kembali
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            {/* Header Card with Main Info */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 4, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white', 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(147, 51, 234, 0.05) 100%)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80,
                      bgcolor: 'primary.main',
                      fontSize: '2rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                      {customer.name || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                      <Chip 
                        label={`Tier ${customer.id_tier || '-'}`}
                        color={getTierColor(customer.id_tier)}
                        icon={<WorkspacePremiumIcon />}
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip 
                        label={getStatusLabel(customer.status)}
                        color={getStatusColor(customer.status)}
                        icon={customer.status === 1 ? <CheckCircleIcon /> : <CancelIcon />}
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, display: 'inline-block' }}>
                      ID: {customer.id_user}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Personal Information */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                height: '100%'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon /> Informasi Personal
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" /> Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, pl: 3 }}>
                      {customer.email || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon fontSize="small" /> Phone
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, pl: 3 }}>
                      {customer.phone || '-'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CakeIcon fontSize="small" /> Tanggal Lahir
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, pl: 3 }}>
                      {formatBirthDate(customer.birth_date)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Gender
                    </Typography>
                    <Box sx={{ pl: 3 }}>
                      {customer.gender ? (
                        <Chip 
                          label={customer.gender}
                          variant="outlined"
                          color="info"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                          Tidak tersedia
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Account Information */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 3, 
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'white', 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                height: '100%'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkspacePremiumIcon /> Informasi Akun
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Tier Level
                    </Typography>
                    <Box sx={{ pl: 0 }}>
                      <Chip 
                        label={`Tier ${customer.id_tier || '-'}`}
                        color={getTierColor(customer.id_tier)}
                        icon={<WorkspacePremiumIcon />}
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Status Akun
                    </Typography>
                    <Box sx={{ pl: 0 }}>
                      <Chip 
                        label={getStatusLabel(customer.status)}
                        color={getStatusColor(customer.status)}
                        icon={customer.status === 1 ? <CheckCircleIcon /> : <CancelIcon />}
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                      Tanggal Dibuat
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDate(customer.date_created)}
                    </Typography>
                  </Box>
                  {customer.date_updated && (
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Tanggal Diupdate
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(customer.date_updated)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalCustomerDetailPage;
